import { Check, ICONO } from '../ui/iconos.js';

type Estado = 'hecho' | 'actual' | 'pendiente';

const CIRCULO: Readonly<Record<Estado, string>> = Object.freeze({
  hecho: 'bg-accent text-heading',
  actual: 'bg-brand text-primary-foreground shadow-halo-navy-100',
  pendiente: 'bg-muted text-subtle',
});

function Paso({
  numero,
  estado,
  ultimo,
  texto,
}: {
  numero: number;
  estado: Estado;
  ultimo: boolean;
  texto: string;
}) {
  return (
    <li
      aria-current={estado === 'actual' ? 'step' : undefined}
      className={`flex items-center gap-2 ${ultimo ? '' : 'flex-1'}`}
    >
      <span
        aria-hidden="true"
        className={`flex size-9 shrink-0 items-center justify-center rounded-full text-md font-bold ${CIRCULO[estado]}`}
      >
        {estado === 'hecho' ? <Check className={ICONO.normal} /> : numero}
      </span>
      <span className="sr-only">{texto}</span>
      {ultimo ? null : (
        <span
          aria-hidden="true"
          className={`h-1 flex-1 rounded-full ${estado === 'hecho' ? 'bg-accent' : 'bg-track'}`}
        />
      )}
    </li>
  );
}

/**
 * El avance por pasos (stepper de la referencia): círculos numerados unidos por
 * una línea.
 *
 * Hecho: teal con el tilde. Actual: navy con halo. Por venir: gris con su número.
 * **El estado no lo lleva sólo el color**: el hecho cambia el número por un
 * tilde y el actual tiene halo, así que se distinguen en escala de grises (WCAG
 * 1.4.1). Es una lista ordenada con `aria-current="step"`, y cada paso tiene su
 * frase para el lector de pantalla, que la pantalla que lo usa trae del copy.
 */
export function Stepper({
  total,
  actual,
  etiqueta,
  textoDelPaso,
}: {
  total: number;
  /** El paso en curso, desde 1. */
  actual: number;
  /** El nombre de la lista para el lector de pantalla. */
  etiqueta: string;
  /** La frase de cada paso para el lector ("Paso 2, actual"). */
  textoDelPaso: (numero: number, estado: Estado) => string;
}) {
  const pasos = Array.from({ length: total }, (_, indice) => indice + 1);

  return (
    <ol aria-label={etiqueta} className="m-0 flex list-none items-center gap-2 p-0">
      {pasos.map((numero) => {
        const estado: Estado =
          numero < actual ? 'hecho' : numero === actual ? 'actual' : 'pendiente';
        return (
          <Paso
            estado={estado}
            key={numero}
            numero={numero}
            texto={textoDelPaso(numero, estado)}
            ultimo={numero === total}
          />
        );
      })}
    </ol>
  );
}
